// src/components/features/Vehicle/MobileVehicleSwitcher/MobileVehicleSwitcher.tsx
// Compact bar displayed on the mobile dashboard showing the currently selected
// vehicle with a "Switch" button (multiple cars) or "Add" button (single car).
// Tapping "Switch" opens the VehiclePickerSheet.

import React, { useCallback } from "react";
import {
  Box,
  Group,
  Paper,
  Text,
  useMantineTheme,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Car } from "@/utils/api/CarService";
import {
  getCarDisplayName,
  getCarSubtitle,
} from "@/utils/carHelpers";

import { AppButton } from "@/components/generic/AppButton";

import { VehicleImage } from "../VehicleImage";
import { VehiclePickerSheet } from "../VehiclePickerSheet";

// ============================================
// Types
// ============================================

export interface MobileVehicleSwitcherProps {
  /** Currently selected car */
  selectedCar: Car | null;

  /** All available cars */
  cars: Car[];

  /** Whether the account has multiple cars */
  hasMultipleCars: boolean;

  /** Called when a different vehicle is selected */
  onSelect: (carId: string) => void;
}

// ============================================
// Component
// ============================================

export const MobileVehicleSwitcher: React.FC<MobileVehicleSwitcherProps> = ({
  selectedCar,
  cars,
  hasMultipleCars,
  onSelect,
}) => {
  const theme = useMantineTheme();
  const navigate = useNavigate();
  const { t } = useTranslation(["common"]);

  const [pickerOpened, { open: openPicker, close: closePicker }] =
    useDisclosure(false);

  const handleAddVehicle = useCallback(() => {
    navigate("/cars/add");
  }, [navigate]);

  // Nothing to show if no car is selected
  if (!selectedCar) {
    return null;
  }

  const displayName = getCarDisplayName(selectedCar);
  const subtitle = getCarSubtitle(selectedCar);
  const selectedCarId = selectedCar.id || null;

  return (
    <>
      <Paper
        p="sm"
        radius="md"
        bg="white"
        style={{
          border: `1px solid ${theme.colors.neutral[2]}`,
        }}
      >
        <Group gap="sm" wrap="nowrap">
          {/* Vehicle image or icon */}
          <VehicleImage
            car={selectedCar}
            size="sm"
            radius="md"
            alt={displayName}
          />

          {/* Vehicle info */}
          <Box style={{ flex: 1, minWidth: 0 }}>
            <Text fw={600} size="md" c="neutral.8" truncate>
              {displayName}
            </Text>
            {subtitle && (
              <Text size="xs" c="neutral.5" truncate lh={1.3}>
                {subtitle}
              </Text>
            )}
          </Box>

          {/* Switch or Add button */}
          {hasMultipleCars ? (
            <AppButton
              variant="secondary"
              size="sm"
              iconName="ArrowsSort"
              onClick={openPicker}
              style={{ flexShrink: 0 }}
            >
              {t("common:dashboard.vehicleSwitcher.switch", {
                defaultValue: "Switch",
              })}
            </AppButton>
          ) : (
            <AppButton
              variant="secondary"
              size="sm"
              iconName="Plus"
              onClick={handleAddVehicle}
              style={{ flexShrink: 0 }}
            >
              {t("common:dashboard.vehicleSwitcher.add", {
                defaultValue: "Add",
              })}
            </AppButton>
          )}
        </Group>
      </Paper>

      {/* Vehicle Picker Sheet (only rendered when multiple cars) */}
      {hasMultipleCars && (
        <VehiclePickerSheet
          opened={pickerOpened}
          onClose={closePicker}
          cars={cars}
          selectedCarId={selectedCarId}
          onSelect={onSelect}
          onAddVehicle={handleAddVehicle}
        />
      )}
    </>
  );
};